import template from 'modal/modal.j2';

import { isEscPressed } from '../utils/keyUtils';
import { error } from '../utils/logger';

import { generate, addChild } from '../../../core/js/utils/componentUtils';
import { getDefaultOptions } from '../../../core/js/utils/a15componentUtils';
import { attachPreventMobileOverScroll, detachPreventMobileOverScroll } from '../../../core/js/utils/eventUtils';
import { isBlurrableElement } from '../../../core/js/utils/generalUtils';

import { popModalTransition, popAllModalTransition } from '../actions/modalActions';

/**
 * modalComponent children will be a list of at LEAST 1 component.
 *
 * If the list has one component, that's a standard modal component
 *   MODAL -> [ LOGINCOMPONENT ]
 *
 *
 *
 * If the list has MORE than one component, that's an internal stack
 *  of modal content components
 *
 *   MODAL -> [ SEARCH, RESULTS ]
 *
 *
 *
 * And the active modal contents component is *ALWAYS* the last on the stack
 */

export function init(store, overrides = {}) {
    const focusedElement = document.activeElement;
    const config = {
        contentsLocation: '.dom-modal-contents',        // Where modal stuff will live
        attachEvents: attachEvents,
        attachWatchers: attachWatchers,
        afterConstruction: afterConstruction,
        template: template,
        name: 'modalComponent',
        replace: false,
        hide: hide,
        reshow: reshow,
        destroyComponent: destroyComponent,
        cancelAction: cancelAction,
        xcloseAction: xcloseAction,
        closeAction: closeAction,
        addChild: addChildOverride,
        external: false,
        overlayHidden: overrides.overlayHidden || false,
        inheritedOverrides: ['external'],
        autoFocus: autoFocus,
        attachModalScrollFix: attachModalScrollFix,
        detachModalScrollFix: detachModalScrollFix,
        uuid: overrides.uuid,
        focusedElement
    };

    overrides.store = store;
    return generate(config, store, overrides, getDefaultOptions(store));
}

export function afterConstruction(store) {
    const templateString = template.render(this);
    $('.dom-modals-anchor').append(templateString);

    // set overflow-scrolling style after content has loaded into modal to prevent iOS scrolling bug
    setTimeout(() => {
        $('.dom-modal').addClass('u-overflow-scrolling');
    }, 500);
}

/**
 * A non first element focusing modal
 * @param {Object} store Redux store
 * @param {Object} overrides overrides
 * @returns {Promise} looks like a promise with the disabled autofocus
 */
export function nonAutoFocusingInit(store, overrides = {}) {
    const nonFocusing = _.merge(overrides, {
        disableAutoFocus: true
    });

    return init(store, nonFocusing);
}

/**
 * Bootstrap the component onto the page
 * @param {Object} store Redux store
 * @returns {undefined}
 */
export function initialize(store) {
    attachEvents(store);

    this.store = store;
}

function attachWatchers(store) { }

/**
 * Attach component events to DOM
 * @param {Object} store Redux store
 * @returns {undefined}
 */
function attachEvents(store) {
    const self = this;

    /**
     * Close(X) button will pop all modals off the stack
     */
    $(document).on(this.scopedEvent('touchend click'), this.scopedSelector('.js-modal__close'), (e) => {
        e.preventDefault();
        self.xcloseAction(store, e);
    });

    $(document).on('MODAL_CLOSE', (e) => {
        e.preventDefault();
        self.xcloseAction(store, e);
    });

    /**
     * Cancel will pop only this modal off the stack
     */
    $(document).on(this.scopedEvent('click'), this.scopedSelector('.js-modal__cancel'), (e) => {
        e.preventDefault();
        self.cancelAction(store, e);
        self.closeAction(store, e);

    });

    $(document).on(this.scopedEvent('keyup'), (e) => {
        e.preventDefault();

        const container = $(this.scopedSelector('.js-modal-inner'));
        if (container.length === 0) {
            // js-modal-inner not on the page.  This is probably an alert modal
            // and shouldn't respond to a escape keypresses
            return;
        }

        if (isEscPressed(e)) {
            const payload = popAllModalTransition(e, this.name);
            $.event.trigger({
                type: 'ESC_KEY_PRESSED'
            });

            $.event.trigger({
                type: payload.type,
                payload: payload
            });
        }
    });

    // trap focus in the modal
    $(document).on(this.scopedEvent('keydown'), (e) => {
        const tabbableElements = $(this.scopedSelector()).find('a[href], input, textarea, select, button, [tabindex=0]')
            .filter(':visible:not(:hidden):not([disabled])');
        const firstTabbableElement = tabbableElements.first()[0];
        const lastTabbableElement = tabbableElements.last()[0];

        if (e.key === 'Tab' || e.keyCode === 9) {
            if (e.shiftKey) {
                if (document.activeElement === firstTabbableElement) {
                    e.preventDefault();
                    lastTabbableElement.focus();
                }
            } else if (document.activeElement === lastTabbableElement) {
                e.preventDefault();
                firstTabbableElement.focus();
            }
        }
    });

    /**
     * Dismiss the modal by clicking outside
     */
    $(document).on(this.scopedEvent('mousedown'), (e) => {
        const container = $(this.scopedSelector('.js-modal-inner'));
        const target = e.target;

        if (container.length === 0) {
            // js-modal-inner not on the page.  This is probably an alert modal
            // and shouldn't respond to a mouse click outside
            return;
        }

        // Is the target of the click in the container?
        const clickIsNotContainer = !container.is(e.target) &&
            container.has(e.target).length === 0;

        /*
         * If the target of the click is not the container
         * and not pikaday/google/etc then close the modal
         */
        if (clickIsNotContainer && !modalElementClickOneOffs.call(this, target)) {
            self.xcloseAction(store, e);
            $.event.trigger({
                type: 'OUTSIDE_MODAL_CLICKED'
            });
        }
    });
}

/**
 * Some elements are clicked that are registered as OUTSIDE the modal
 * (for various reasons)
 *
 * Handling these one off situations here
 * @param {string} target jquery selector
 * @returns {boolean} true if one of the clicks
 */
function modalElementClickOneOffs(target) {
    const targetClasses = $(target).attr('class') || [];

    const pikadayContainer = $(this.scopedSelector('.js-delivery-date-calendar-container'));
    // Is the target of the click in the pikaday module?
    const clickIsPikaday = pikadayContainer.parents(target) &&
        targetClasses.includes('pika');

    // Google's address auto-complete container is a child of <body>, not the modal
    const googleContainer = $('.pac-container');
    const clickIsGoogle = googleContainer.parents(target) &&
        targetClasses.includes('pac-');

    // Google version in checkout seems behave just a bit differently
    /* eslint-disable newline-per-chained-call */
    const parentClasses = $(target).parent().attr('class') || [];
    /* eslint-enable newline-per-chained-call */
    const clickIsGoogle2 = parentClasses.includes('pac-');

    const clickIsCookieAlert = $(target).closest('.js-cookies-alert').length;

    return clickIsPikaday || clickIsGoogle || clickIsGoogle2 || clickIsCookieAlert;
}

/**
 * Override the addChild function from componentUtils.
 *
 * NOTE:
 * Modals should only be adding one child, so this *should* be the end of the render loop
 *
 * Once the child is added and rendered (assuming it has a render, which
 *  all modals should have) this will attempt to focus the form correctly
 *
 * @param {Object} child ???
 * @param {Object} store Redux store
 * @returns {Promise} addedChild
 */
function addChildOverride(child, store) {
    const addedChild = addChild.call(this, child, store);

    const isTouchDevice = _.get(this.getDeviceCharacteristics(), 'is_touch', false)
                                || navigator.userAgent === 'URBNWebView';

    const inputs = $(this.scopedSelector('.dom-modal-contents form'))
        .find('input,textarea,select')
        .filter(':visible:first');

    this.autoFocus(inputs);

    // if using touch device, scroll to top of modal
    if (isTouchDevice) {
        $('.dom-modal').animate({
            scrollTop: 0
        }, 100, () => {
            setTimeout(() => {
                if (inputs && inputs.length > 0) {
                    inputs[0].blur();
                    inputs[0].focus();
                    $('.dom-modal').scrollTop(1);
                }
            }, 0);
        });

        $('html, body').animate({
            scrollTop: 1
        }, 100);
    }

    /* bind touch device overscroll prevention */
    this.attachModalScrollFix();

    return addedChild;
}

function attachModalScrollFix() {
    const deviceCharacteristics = this.getDeviceCharacteristics();
    const isTouchDevice = _.get(deviceCharacteristics, 'is_touch', false);
    const isSmallTouchDevice = isTouchDevice && _.get(deviceCharacteristics, 'is_smallish', false);
    const isLargeTouchDevice = isTouchDevice &&
                                (_.get(deviceCharacteristics, 'is_medium', false)
                                    || _.get(deviceCharacteristics, 'is_large', false)
                                    || _.get(deviceCharacteristics, 'is_extra_large', false));

    // if small touch device - attach small screen scroll fix
    if(isSmallTouchDevice) {
        attachPreventMobileOverScroll(this.scopedEvent('touchstart'),
                                        this.scopedEvent('touchmove'),
                                        this.scopedSelector(), '.o-modal');

    // if large touch device - attach medium - xl scroll fix
    } else if(isLargeTouchDevice) {

        const containerElement = $('.js-modal-inner');
        if(containerElement.length) {
            // check for a div that is taller than the container
            const scrollableModalElement = findScrollableModalElement(containerElement);
            if(scrollableModalElement) {
                let scrollableClassSelector = '.' + $(scrollableModalElement).attr("class");
                scrollableClassSelector = scrollableClassSelector
                                            .replace(/\s\s+/g, ' ')
                                            .trim()
                                            .split(' ')
                                            .join('.');
                attachPreventMobileOverScroll(
                    this.scopedEvent('touchstart'),
                    this.scopedEvent('touchmove'),
                    this.scopedSelector(),
                    scrollableClassSelector
                );
            }
        }
    }
}

function findScrollableModalElement(containerElement) {
    let tallestChildElement;
    containerElement.find('*').each((index, elem) => {
        const tallestChildHeight = tallestChildElement ? tallestChildElement.prop('scrollHeight') : 0;
        tallestChildElement = $(elem).prop('scrollHeight') > tallestChildHeight ? $(elem) : tallestChildElement;
    });
    return tallestChildElement.prop('scrollHeight') > containerElement.outerHeight(true) ? tallestChildElement : false;
}

function detachModalScrollFix() {
    const isTouchDevice = _.get(this.getDeviceCharacteristics(), 'is_touch', false);

    if (isTouchDevice) {
        detachPreventMobileOverScroll(
            this.scopedEvent('touchstart'),
            this.scopedEvent('touchmove'),
            this.scopedSelector()
        );
    }
}

/**
 * For accessibility we must always at least get focus into the modal, so
 * 'disableAutoFocus' only applies to selecting the first form element
 *
 * @param {Array} inputs jQuery objects of all inputs within the modal
 */
function autoFocus(inputs) {
    // After the modal is constructed, focus to at least the contents.
    // This should always exist.
    let thingToFocus = $(this.scopedSelector('.dom-modal-contents')).first();

    // Only auto focus first field/form when appropriate
    if (!this.disableAutoFocus) {
        // focus a form if possible
        const modalForms = $(this.scopedSelector('.dom-modal-contents form'));
        if (modalForms.length > 0) {
            thingToFocus = modalForms[0];
        }

        // finally, try the first input field
        if (inputs.length > 0) {
            thingToFocus = inputs[0];
        } else if (document.activeElement && document.activeElement.nodeName !== "svg") {
            // Null check and SVG check for ie11
            document.activeElement.blur();
        }
    }

    thingToFocus.focus();
}

function cancelAction(store, e) {
    // Common modal cancel actions.  Nothing to see here yet

    _.forEach(this.childrenObjects, (child) => {
        if (child.afterCancelAction) {
            child.afterCancelAction(store, e);
        }
    });
}

/**
 * Modals can be closed in 3 ways:
 *
 * Call the closeAction on the modal
 *  OR
 * popModalTransition Event
 *  OR
 * popAllModalTransition Event
 *
 * @param {Object} store Redux store
 * @param {Object} e Event that fired this
 */
function closeAction(store, e) {
    if (this.name !== 'modalComponent') {
        error("modalComponent.closeAction should never be called by anything other than modalComponent");
    }

    const payload = popModalTransition();

    $.event.trigger({
        type: payload.type,
        payload: payload
    });
}

/**
 * Action called if the modal is X'd out
 * @param {Object} store Redux store
 * @param {Object} e Event that fired this
 */
function xcloseAction(store, e) {
    _.forEach(this.childrenObjects, (child) => {
        if (child.afterXCloseAction) {
            child.afterXCloseAction(store, e);
        }
    });

    const payload = popAllModalTransition(e, this.name);
    $.event.trigger({
        type: payload.type,
        payload: payload
    });
}

/*
 * This is called AFTER a modal is closed by
 *  closeAction/popModalTransition/popAllModalTransition
 */
function destroyComponent(store, e) {
    _.forEach(this.childrenObjects, (child) => {
        if (child.afterCloseAction) {
            child.afterCloseAction(store, e);
        }
    });

    /* Unbind touch device overscroll prevention */
    this.detachModalScrollFix();

    this.removeAllChildren();
    this.removeEvents(store);

    $(this.templateLocation).remove();

    // re-focus the element that was focused when you launched the modal
    $(this.focusedElement).focus();
}

/**
 * Re-show, re-activate a modal that was once hidden
 */
function reshow() {
    // Reactivate events??
    $(this.templateLocation).addClass('is-shown');

    this.attachEvents(this.store);

    const self = this;
    _.forEach(this.childrenObjects, (child) => {
        child.attachEvents(self.store);
    });
}

/**
 * Hide a modal.  This happens when a new modal appears *or*
 *  or a modal is closed
 */

function hide(store) {
    $(this.templateLocation).removeClass('is-shown');
    this.removeEvents(store);
    /* Make sure to close any open/focused dropdowns/keyboards when closing the modal  */
    if (document.activeElement && isBlurrableElement(document.activeElement)) {
        document.activeElement.blur();
    }

    const self = this;
    _.forEach(this.childrenObjects, (child) => {
        child.removeEvents(self.store);
    });
}
